import { HashHistory, Update } from 'history';
import { useEffect, useMemo, useState } from 'react';
import { history } from 'src/utils/widget/setup';

export function Router({
    onChange,
    children,
    name,
}: {
    onChange?: (update: Update, history: HashHistory) => void;
    children: any;
    name?: string;
}) {
    const [currentPath, setCurrentPath] = useState(window.location.hash?.slice(1) ?? '/');

    // Use a temporary variable as "cache", because there could be rapid
    // history-pushes that happen faster than the react render-cycle.
    // When rapid pushes occur the useEffect below will read from this
    // variable multiple times before the render-cycle has a chance to
    // catch up. So by using this cache-variable the useEffect will
    // always have the most up-to-date value.

    let _currentPath = currentPath;

    useEffect(() => {
        const unlisten = history.listen((update) => {
            const newPath = update.location.pathname;

            // If the new path is the same as the current, skip.
            if (_currentPath === newPath) return;

            if (onChange) onChange(update, history);

            // eslint-disable-next-line react-hooks/exhaustive-deps
            _currentPath = newPath;

            setCurrentPath(newPath);
        });
        return unlisten;
    }, [onChange, currentPath, _currentPath, name]);

    return children.filter((child: any) => child.props.path === currentPath);
}

export function Route({ children }: { path: string; children: any }) {
    return children;
}

export function route(path: string) {
    history.push(path, { pathname: path });
}

export function useQueryParams() {
    const { hash } = window.location;
    return useMemo(() => {
        const params = new URLSearchParams(hash.split('?')[1]);
        return Object.fromEntries(params.entries());
    }, [hash]);
}
